/** @format */
import {useState} from 'react';

import {Trans, useTranslation} from 'react-i18next';
import {Check, CreditCardOutlined, SupportAgentOutlined} from '@mui/icons-material';
import {Box, LinearProgress, Typography} from '@mui/material';
import {Navigate} from 'react-router-dom';
import {format} from 'date-fns';
import {ru} from 'date-fns/locale';
import Alert from '@mui/material/Alert';

import Breadcrumbs from 'components/Breadcrumbs';
import Modal from 'components/Modal';
import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';
import {useApolloError} from 'helpers/hooks/useApolloError';
import {parseEndDate} from 'features/tariffs/helpers/parseEndDate';
import {isTrialSubscription} from 'features/tariffs/helpers/isTrialSubscription';
import {useMixpanelContext} from 'contexts/MixpanelContext';
import PurchaseAiReportsModal from 'components/PurchaseAiReportsModal';

import {
  BlockWrap,
  ButtonBuy,
  ButtonsBox,
  ButtonSupport,
  ButtonText,
  DescriptionWrap,
  HeadingBox,
  HeadingText,
  ModalContent,
  RegularText,
  TariffBlock,
  TariffBorderBlock,
  TariffCard,
  TariffNameText,
} from './styles';
import {BREADCRUMBS} from './constants';
import {useGetTariffDetailsQuery, useRequestBillingSupportMutation} from './graphql/__generated__';
import {useGetTariffInfo} from './hooks/useGetTariffInfo';

const Tariffs = () => {
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);

  const {t} = useTranslation();

  const {isError, onError, renderAlert} = useApolloError();

  const [requestSupport, {loading: loadingSupport}] = useRequestBillingSupportMutation();

  const {mixpanel} = useMixpanelContext();
  const [purchaseModalOpen, setPurchaseModalOpen] = useState<boolean>(false);

  const {data, error, loading} = useGetTariffDetailsQuery();
  const unitPrice = data?.currentUser?.subscription.tariffDetails?.aiReportsUnitPrice ?? 0;

  const tariffInfo = useGetTariffInfo({
    maxAiReportRuns: data?.currentUser?.subscription.tariffDetails?.maxAiReportRuns,
    maxSubAccounts: data?.currentUser?.subscription.tariffDetails?.maxSubAccounts,
    spentMax: data?.currentUser?.subscription.tariffDetails?.spentMax,
  });

  const {parsedEndDate, isValid: isValidDate} = parseEndDate(
    data?.currentUser?.subscription.endDate,
  );
  const isParseError = error || (!loading && !isValidDate);

  if (isParseError) {
    return <Alert severity='error'>Что-то пошло не так</Alert>;
  }

  if (loading) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const isTrial = isTrialSubscription(data?.currentUser?.subscription);

  if (isTrial) {
    return <Navigate to={{pathname: '/tariffs'}} />;
  }

  const {cabinets, budget, aiReports} = tariffInfo;

  const tariffName = data?.currentUser?.subscription.tariffDetails?.name || '';
  const tariffKey = data?.currentUser?.subscription.tariffDetails?.key;
  const isArchived = tariffKey === 'pro';
  const isUltraTariff = tariffKey === 'ultra' || tariffKey === 'ultra_pro';
  const remainingReports = data?.currentUser?.aiReportsLimit.remainingReports;

  const expiresString = parsedEndDate ? format(parsedEndDate, 'd MMMM yyyy', {locale: ru}) : '';

  const isBusy = loadingSupport || isOpenModal || purchaseModalOpen || isError;
  const disableSupport = isBusy;
  const disableBuy = isBusy || unitPrice <= 0;

  const handleRequestSupport = async () => {
    try {
      const response = await requestSupport();
      if (response.data?.requestBillingSupport) {
        setIsOpenModal(true);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      onError();
    }
  };

  const handleOpenPurchaseModal = () => {
    mixpanel.track('ai_reports_purchase_modal_open', {from_state: 'tariffs'});
    setPurchaseModalOpen(true);
  };

  const handleClosePurchaseModal = () => setPurchaseModalOpen(false);

  return (
    <Box>
      <Breadcrumbs items={BREADCRUMBS} />

      <HeadingBox>
        <HeadingText>
          <Trans>Доступен полный функционал</Trans>
        </HeadingText>
        <Check />
      </HeadingBox>

      <TariffCard>
        <TariffBorderBlock>
          <TariffBlock>
            <BlockWrap>
              <RegularText>
                <Trans>Текущий тариф</Trans>
              </RegularText>
              <DescriptionWrap>
                <TariffNameText>{tariffName}</TariffNameText>
                {!isUltraTariff && cabinets ? <RegularText>{cabinets}</RegularText> : null}
                {!isUltraTariff && aiReports ? <RegularText>{aiReports}</RegularText> : null}
                {!isUltraTariff && budget ? <RegularText>{budget}</RegularText> : null}
              </DescriptionWrap>
            </BlockWrap>
          </TariffBlock>

          <TariffBlock>
            <BlockWrap>
              <RegularText>
                <Trans>Подписка</Trans>
              </RegularText>
              <DescriptionWrap>
                <RegularText>
                  <Trans>До</Trans> {expiresString}
                </RegularText>
              </DescriptionWrap>
            </BlockWrap>
          </TariffBlock>

          <TariffBlock>
            <BlockWrap>
              <RegularText>
                <Trans>
                  {isArchived ? 'Осталось ИИ-исследований' : 'Доступно ИИ-исследований'}
                </Trans>
              </RegularText>
              <DescriptionWrap>
                <RegularText>{remainingReports ?? 0}</RegularText>
              </DescriptionWrap>
            </BlockWrap>
          </TariffBlock>
        </TariffBorderBlock>

        <ButtonsBox>
          <ButtonSupport onClick={handleRequestSupport} disabled={disableSupport}>
            <SupportAgentOutlined fontSize='small' />
            <ButtonText>
              <Trans>Связаться с поддержкой</Trans>
            </ButtonText>
          </ButtonSupport>

          <ButtonBuy onClick={handleOpenPurchaseModal} disabled={disableBuy}>
            <CreditCardOutlined fontSize='small' />
            <ButtonText>
              <Trans>Купить дополнительные ИИ-исследования</Trans>
            </ButtonText>
          </ButtonBuy>
        </ButtonsBox>

        {renderAlert}
      </TariffCard>

      <PurchaseAiReportsModal
        open={purchaseModalOpen}
        onClose={handleClosePurchaseModal}
        unitPrice={unitPrice}
      />

      {isOpenModal && !isError && (
        <Modal header={t('Запрос отправлен')} onClose={() => setIsOpenModal(false)}>
          <ModalContent>
            <Typography>
              <Trans>Мы получили заявку и свяжемся с вами в кратчайшие сроки</Trans>
            </Typography>
            <Box
              sx={{
                mt: 8,
              }}
            >
              <PrimaryMediumButton fullWidth onClick={() => setIsOpenModal(false)}>
                <Trans>Окей</Trans>
              </PrimaryMediumButton>
            </Box>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export {Tariffs};
